import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiBookOpen,
  FiArrowLeft,
  FiCalendar,
  FiCheckSquare,
  FiChevronRight,
  FiHelpCircle,
  FiHome,
  FiInfo,
  FiLink,
  FiList,
  FiLogOut,
  FiSettings,
  FiSearch,
  FiShield,
  FiUser,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import http from "../../../api/apiClient";
import { LinkIcon } from "../../../components/ClassLinksSection";
import ParentActionsSubnav, { ParentActionsView } from "./ParentActionsSubnav";

type TabItem = {
  key: string;
  label: string;
};

type SchoolLink = {
  id: string;
  title: string;
  url: string;
  description?: string | null;
};

interface ParentPortalMobileNavProps {
  tabItems: TabItem[];
  activeTab: string;
  setActiveTab: (key: any) => void;
  onLogout?: () => void;
  onOpenAdminPortal?: () => void;
  onOpenProfile?: () => void;
  onOpenSettings?: () => void;
  onOpenCalendarLink?: () => void;
  adminPortalPath?: string;
  showAdminPortal?: boolean;
  appVersion?: string;
  directorySearch?: {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    loading?: boolean;
    placeholder?: string;
  };
  actionsView: ParentActionsView;
  setActionsView: (view: ParentActionsView) => void;
  openActionsCount: number;
  schoolTasksCount: number | null;
  showSchoolTasks: boolean;
  openActionsLabel: string;
  schoolTasksLabel: string;
}

const InfoIcon = FiInfo as any;
const FamilyIcon = FiUsers as any;
const CalendarIcon = FiCalendar as any;
const DirectoryIcon = FiBookOpen as any;
const MoreIcon = FiList as any;
const ProfileIcon = FiUser as any;
const ActionsIcon = FiCheckSquare as any;
const SchoolIcon = FiHome as any;
const CalendarLinkIcon = FiLink as any;
const AdminIcon = FiShield as any;
const HelpIcon = FiHelpCircle as any;
const SettingsIcon = FiSettings as any;
const LogoutIcon = FiLogOut as any;
const ChevronIcon = FiChevronRight as any;

const ICONS: Record<string, any> = {
  info: InfoIcon,
  family: FamilyIcon,
  calendar: CalendarIcon,
  directory: DirectoryIcon,
};

const DEFAULT_PRIMARY_KEYS = ["info", "family", "calendar", "directory"];

const hasTab = (tabs: TabItem[], key: string) => tabs.some((tab) => tab.key === key);

export default function ParentPortalMobileNav({
  tabItems,
  activeTab,
  setActiveTab,
  onLogout,
  onOpenAdminPortal,
  onOpenProfile,
  onOpenSettings,
  onOpenCalendarLink,
  adminPortalPath = "/",
  showAdminPortal = false,
  appVersion,
  directorySearch,
  actionsView,
  setActionsView,
  openActionsCount,
  schoolTasksCount,
  showSchoolTasks,
  openActionsLabel,
  schoolTasksLabel,
}: ParentPortalMobileNavProps) {
  const { t } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [moreView, setMoreView] = useState<"main" | "school-links">("main");
  const [schoolLinks, setSchoolLinks] = useState<SchoolLink[]>([]);
  const [schoolLinksLoading, setSchoolLinksLoading] = useState(false);
  const [schoolLinksError, setSchoolLinksError] = useState("");
  const [directorySearchOpen, setDirectorySearchOpen] = useState(activeTab === "directory");
  const directoryInputRef = useRef<HTMLInputElement | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const touchStartYRef = useRef<number | null>(null);

  const primaryTabs = useMemo(() => {
    const orderedTabs = DEFAULT_PRIMARY_KEYS
      .map((key) => tabItems.find((tab) => tab.key === key))
      .filter(Boolean) as TabItem[];

    return orderedTabs;
  }, [tabItems]);

  useEffect(() => {
    if (!moreOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMoreOpen(false);
        setMoreView("main");
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.classList.add("pp-mobile-more-is-open");

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("pp-mobile-more-is-open");
    };
  }, [moreOpen]);

  useEffect(() => {
    setDirectorySearchOpen(activeTab === "directory");
  }, [activeTab]);

  useEffect(() => {
    if (directorySearchOpen && activeTab === "directory") {
      window.setTimeout(() => directoryInputRef.current?.focus(), 0);
    }
  }, [activeTab, directorySearchOpen]);

  const openTab = (key: string) => {
    if (hasTab(tabItems, key)) setActiveTab(key);
    setMoreOpen(false);
  };

  const openSchoolLinks = async () => {
    setMoreView("school-links");
    setSchoolLinksLoading(true);
    setSchoolLinksError("");
    try {
      const { data } = await http.get<{ links?: SchoolLink[] }>("/school-links");
      setSchoolLinks(data?.links || []);
    } catch {
      setSchoolLinksError(t("school.links.load_failed", { defaultValue: "School-wide links could not be loaded." }));
    } finally {
      setSchoolLinksLoading(false);
    }
  };

  const openProfile = () => {
    openTab("info");
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("parentPortal:openMyDetails"));
    }, 0);
  };

  const openSettings = () => {
    // Instellingen opent het accountprofiel: gebruikersnaam, taal, wachtwoord en notificaties.
    if (onOpenSettings) {
      onOpenSettings();
      setMoreOpen(false);
      return;
    }

    if (hasTab(tabItems, "preferences")) {
      openTab("preferences");
      return;
    }

    if (onOpenProfile) {
      onOpenProfile();
      setMoreOpen(false);
      return;
    }

    openTab("info");
  };

  const openCalendarLink = () => {
    if (onOpenCalendarLink) {
      onOpenCalendarLink();
      setMoreOpen(false);
      return;
    }

    openTab("calendar");
  };

  const openAdminPortal = () => {
    if (onOpenAdminPortal) {
      onOpenAdminPortal();
      setMoreOpen(false);
      return;
    }

    window.location.href = adminPortalPath;
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
      setMoreOpen(false);
      return;
    }

    window.location.href = "/logout";
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartYRef.current = event.touches[0]?.clientY ?? null;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    const startY = touchStartYRef.current;
    touchStartYRef.current = null;

    if (startY === null) return;

    const endY = event.changedTouches[0]?.clientY ?? startY;
    const deltaY = endY - startY;

    if (deltaY < -35) setMoreOpen(true);
    if (deltaY > 35) setMoreOpen(false);
  };

  const menuSections = [
    {
      title: null,
      items: [
        {
          key: "my-details",
          label: t("parentPortal.my_details", { defaultValue: "My details" }),
          Icon: ProfileIcon,
          onClick: openProfile,
        },
        {
          key: "actions",
          label: t("parentPortal.tabs.actions", { defaultValue: "Actions" }),
          Icon: ActionsIcon,
          onClick: () => {
            setActionsView("open");
            setMoreOpen(false);
          },
        },
        {
          key: "school",
          label: t("parentPortal.school.title", { defaultValue: "School details" }),
          Icon: SchoolIcon,
          onClick: () => openTab("info"),
        },
        {
          key: "calendar-link",
          label: t("parentPortal.calendar.connect_title", { defaultValue: "Connect calendar" }),
          Icon: CalendarLinkIcon,
          onClick: openCalendarLink,
        },
        {
          key: "school-links",
          label: t("school.links.title", { defaultValue: "School-wide links" }),
          Icon: CalendarLinkIcon,
          onClick: openSchoolLinks,
        },
      ],
    },
    {
      title: t("parentPortal.mobile_nav.management", { defaultValue: "Management" }),
      hidden: !showAdminPortal,
      items: [
        {
          key: "admin",
          label: t("parentPortal.mobile_nav.admin_portal", { defaultValue: "Teacher portal" }),
          Icon: AdminIcon,
          badge: t("parentPortal.mobile_nav.teacher_badge", { defaultValue: "Teacher" }),
          onClick: openAdminPortal,
        },
      ],
    },
    {
      title: t("parentPortal.mobile_nav.other", { defaultValue: "Other" }),
      items: [
        {
          key: "help",
          label: t("parentPortal.mobile_nav.help", { defaultValue: "Help" }),
          Icon: HelpIcon,
          onClick: () => openTab("info"),
        },
        {
          key: "settings",
          label: t("parentPortal.settings.title", { defaultValue: "Settings" }),
          Icon: SettingsIcon,
          onClick: openSettings,
        },
        {
          key: "logout",
          label: t("common.logout", { defaultValue: "Log out" }),
          Icon: LogoutIcon,
          danger: true,
          onClick: handleLogout,
        },
      ],
    },
  ];

  return (
    <>
      <style>{`
        .pp-mobile-more-is-open {
          overflow: hidden;
        }

        .pp-mobile-nav {
          bottom: max(0.75rem, env(safe-area-inset-bottom));
          left: 0;
          pointer-events: none;
          position: fixed;
          right: 0;
          transform: translateY(0);
          transition: opacity 180ms ease, transform 220ms ease;
          z-index: 70;
        }

        .pp-mobile-nav.is-hidden {
          opacity: 0;
          pointer-events: none;
          transform: translateY(120%);
        }

        .pp-mobile-nav-inner {
          align-items: stretch;
          background: rgba(255, 255, 255, 0.94);
          border: 1px solid rgba(226, 232, 240, 0.9);
          border-radius: 2rem;
          box-shadow: 0 18px 44px rgba(15, 23, 42, 0.14);
          display: grid;
          gap: 0.25rem;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          margin: 0 auto;
          max-width: 34rem;
          padding: 0.45rem;
          pointer-events: auto;
          width: calc(100% - 1.5rem);
        }

        .pp-mobile-nav-search {
          align-items: center;
          background: rgba(255, 255, 255, 0.96);
          border: 1px solid rgba(226, 232, 240, 0.9);
          border-radius: 2rem;
          box-shadow: 0 18px 44px rgba(15, 23, 42, 0.16);
          display: grid;
          gap: 0.75rem;
          grid-template-columns: auto minmax(0, 1fr) auto;
          margin: 0 auto;
          max-width: 34rem;
          min-height: 4.65rem;
          padding: 0.55rem 0.7rem 0.55rem 1rem;
          pointer-events: auto;
          width: calc(100% - 1.5rem);
        }

        .pp-mobile-nav-search > svg {
          color: #059669;
          height: 1.45rem;
          width: 1.45rem;
        }

        .pp-mobile-nav-search input {
          background: transparent;
          border: 0;
          color: #0f172a;
          font: inherit;
          font-size: 1rem;
          font-weight: 700;
          min-width: 0;
          outline: none;
          width: 100%;
        }

        .pp-mobile-nav-search input::placeholder {
          color: #94a3b8;
        }

        .pp-mobile-nav-search-close {
          align-items: center;
          background: #ecfdf5;
          border: 0;
          border-radius: 999px;
          color: #047857;
          display: inline-flex;
          height: 3.3rem;
          justify-content: center;
          width: 3.3rem;
        }

        .pp-mobile-nav-item {
          align-items: center;
          appearance: none;
          background: transparent;
          border: 0;
          border-radius: 1.35rem;
          color: #64727d;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          font: inherit;
          font-size: 0.72rem;
          font-weight: 700;
          gap: 0.25rem;
          justify-content: center;
          min-height: 4.2rem;
          min-width: 0;
          padding: 0.45rem 0.25rem;
          transition: background 160ms ease, color 160ms ease, transform 160ms ease;
        }

        .pp-mobile-nav-item:active {
          transform: scale(0.98);
        }

        .pp-mobile-nav-item.is-active {
          background: linear-gradient(180deg, #d9faeb 0%, #ecfff6 100%);
          color: #047857;
        }

        .pp-mobile-nav-icon {
          height: 1.35rem;
          width: 1.35rem;
        }

        .pp-mobile-nav-label {
          line-height: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 100%;
        }

        .pp-mobile-more-overlay {
          background: rgba(15, 23, 42, 0.48);
          inset: 0;
          opacity: 0;
          pointer-events: none;
          position: fixed;
          transition: opacity 220ms ease;
          z-index: 60;
        }

        .pp-mobile-more-overlay.is-open {
          opacity: 1;
          pointer-events: auto;
        }

        .pp-mobile-more-sheet {
          background: rgba(255, 255, 255, 0.96);
          border: 1px solid rgba(226, 232, 240, 0.9);
          border-radius: 2rem 2rem 0 0;
          bottom: 0;
          box-shadow: 0 -24px 60px rgba(15, 23, 42, 0.18);
          left: 50%;
          max-height: min(78vh, 43rem);
          max-width: 34rem;
          overflow: auto;
          padding: 0.85rem 1.25rem calc(1.35rem + env(safe-area-inset-bottom));
          position: fixed;
          transform: translate(-50%, 105%);
          transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
          width: 100%;
          z-index: 65;
        }

        .pp-mobile-more-sheet.is-open {
          transform: translate(-50%, 0);
        }

        .pp-mobile-more-handle {
          background: #aab3bd;
          border-radius: 999px;
          height: 0.28rem;
          margin: 0 auto 1.35rem;
          width: 3.4rem;
        }

        .pp-mobile-more-title {
          color: #0f172a;
          font-size: 1.35rem;
          font-weight: 800;
          letter-spacing: -0.03em;
          margin: 0 0 1.15rem;
        }

        .pp-mobile-more-section {
          margin-top: 1.15rem;
        }

        .pp-mobile-more-section-title {
          color: #64748b;
          font-size: 0.78rem;
          font-weight: 800;
          margin: 0 0 0.55rem 0.25rem;
        }

        .pp-mobile-more-list {
          background: rgba(255, 255, 255, 0.92);
          border: 1px solid rgba(226, 232, 240, 0.78);
          border-radius: 1rem;
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
          overflow: hidden;
        }

        .pp-mobile-more-row {
          align-items: center;
          appearance: none;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(226, 232, 240, 0.78);
          color: #0f172a;
          cursor: pointer;
          display: grid;
          font: inherit;
          grid-template-columns: auto 1fr auto auto;
          gap: 0.8rem;
          min-height: 3.75rem;
          padding: 0 1rem;
          text-align: left;
          width: 100%;
        }

        .pp-mobile-more-row:last-child {
          border-bottom: 0;
        }

        .pp-mobile-more-row:active {
          background: rgba(236, 253, 245, 0.8);
        }

        .pp-mobile-more-row-icon {
          color: #059669;
          height: 1.18rem;
          width: 1.18rem;
        }

        .pp-mobile-more-row.is-danger .pp-mobile-more-row-icon {
          color: #ef4444;
        }

        .pp-mobile-more-row-label {
          font-size: 0.95rem;
          font-weight: 750;
        }

        .pp-mobile-more-row-badge {
          background: #dff8ec;
          border-radius: 999px;
          color: #047857;
          font-size: 0.68rem;
          font-weight: 800;
          padding: 0.28rem 0.5rem;
        }

        .pp-mobile-more-row-chevron {
          color: #94a3b8;
          height: 1.05rem;
          width: 1.05rem;
        }

        .pp-mobile-more-footer {
          color: #64748b;
          font-size: 0.78rem;
          font-weight: 650;
          line-height: 1.45;
          margin: 1.4rem 0 0;
          text-align: center;
        }

        .pp-mobile-more-subheader {
          align-items: center;
          display: flex;
          gap: 0.75rem;
          margin: 0 0 1rem;
        }

        .pp-mobile-more-back {
          align-items: center;
          background: #ecfdf5;
          border: 0;
          border-radius: 999px;
          color: #047857;
          display: inline-flex;
          height: 2.5rem;
          justify-content: center;
          width: 2.5rem;
        }

        .pp-mobile-more-subheader .pp-mobile-more-title {
          margin: 0;
        }

        .pp-mobile-school-link {
          align-items: center;
          color: #0f172a;
          display: grid;
          gap: 0.8rem;
          grid-template-columns: auto minmax(0, 1fr) auto;
          min-height: 4.25rem;
          padding: 0.75rem 1rem;
          text-decoration: none;
        }

        .pp-mobile-school-link + .pp-mobile-school-link {
          border-top: 1px solid rgba(226, 232, 240, 0.78);
        }

        .pp-mobile-school-link-icon {
          color: #059669;
        }

        .pp-mobile-school-link-copy {
          min-width: 0;
        }

        .pp-mobile-school-link-title {
          display: block;
          font-size: 0.95rem;
          font-weight: 800;
        }

        .pp-mobile-school-link-description {
          color: #64748b;
          display: block;
          font-size: 0.78rem;
          margin-top: 0.15rem;
        }

        .dark .pp-mobile-nav-inner {
          background: rgba(15, 23, 42, 0.96);
          border-color: rgba(51, 65, 85, 0.95);
          box-shadow: 0 18px 44px rgba(0, 0, 0, 0.38);
        }

        .dark .pp-mobile-nav-search {
          background: rgba(15, 23, 42, 0.97);
          border-color: rgba(71, 85, 105, 0.95);
          box-shadow: 0 18px 44px rgba(0, 0, 0, 0.4);
        }

        .dark .pp-mobile-nav-search input {
          color: #f8fafc;
        }

        .dark .pp-mobile-nav-search input::placeholder {
          color: #94a3b8;
        }

        .dark .pp-mobile-nav-search-close {
          background: rgba(6, 78, 59, 0.7);
          color: #a7f3d0;
        }

        .dark .pp-mobile-nav-item {
          color: #94a3b8;
        }

        .dark .pp-mobile-nav-item.is-active {
          background: linear-gradient(180deg, rgba(6, 78, 59, 0.92), rgba(6, 95, 70, 0.68));
          color: #a7f3d0;
        }

        .dark .pp-mobile-more-overlay {
          background: rgba(2, 6, 23, 0.72);
        }

        .dark .pp-mobile-more-sheet {
          background: rgba(15, 23, 42, 0.98);
          border-color: rgba(51, 65, 85, 0.95);
          box-shadow: 0 -24px 60px rgba(0, 0, 0, 0.5);
        }

        .dark .pp-mobile-more-handle {
          background: #475569;
        }

        .dark .pp-mobile-more-title,
        .dark .pp-mobile-more-row {
          color: #f8fafc;
        }

        .dark .pp-mobile-more-section-title,
        .dark .pp-mobile-more-footer {
          color: #94a3b8;
        }

        .dark .pp-mobile-more-list {
          background: rgba(30, 41, 59, 0.92);
          border-color: rgba(71, 85, 105, 0.85);
          box-shadow: 0 10px 28px rgba(0, 0, 0, 0.25);
        }

        .dark .pp-mobile-more-row {
          border-bottom-color: rgba(71, 85, 105, 0.75);
        }

        .dark .pp-mobile-more-row:active {
          background: rgba(6, 78, 59, 0.45);
        }

        .dark .pp-mobile-more-row-badge {
          background: rgba(6, 78, 59, 0.72);
          color: #a7f3d0;
        }

        .dark .pp-mobile-more-back {
          background: rgba(6, 78, 59, 0.65);
          color: #a7f3d0;
        }

        .dark .pp-mobile-school-link {
          color: #f8fafc;
        }

        .dark .pp-mobile-school-link + .pp-mobile-school-link {
          border-top-color: rgba(71, 85, 105, 0.75);
        }

        .dark .pp-mobile-school-link-description {
          color: #94a3b8;
        }
      `}</style>

      <div
        className={`pp-mobile-more-overlay ${moreOpen ? "is-open" : ""}`}
        onClick={() => {
          setMoreOpen(false);
          setMoreView("main");
        }}
        aria-hidden="true"
      />

      <div
        ref={sheetRef}
        className={`pp-mobile-more-sheet ${moreOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={t("parentPortal.mobile_nav.more", { defaultValue: "More" })}
      >
        <div className="pp-mobile-more-handle" aria-hidden="true" />
        {moreView === "school-links" ? (
          <>
            <div className="pp-mobile-more-subheader">
              <button type="button" className="pp-mobile-more-back" onClick={() => setMoreView("main")} aria-label={t("common.back", { defaultValue: "Back" })}>
                <FiArrowLeft aria-hidden="true" />
              </button>
              <h2 className="pp-mobile-more-title">{t("school.links.title", { defaultValue: "School-wide links" })}</h2>
            </div>
            <div className="pp-mobile-more-list">
              {schoolLinksLoading ? (
                <p className="pp-mobile-more-footer">{t("common.loading", { defaultValue: "Loading..." })}</p>
              ) : schoolLinksError ? (
                <p className="pp-mobile-more-footer">{schoolLinksError}</p>
              ) : schoolLinks.length ? schoolLinks.map((link) => (
                <a key={link.id} className="pp-mobile-school-link" href={link.url} target="_blank" rel="noopener noreferrer">
                  <span className="pp-mobile-school-link-icon"><LinkIcon url={link.url} /></span>
                  <span className="pp-mobile-school-link-copy">
                    <span className="pp-mobile-school-link-title">{link.title}</span>
                    {link.description ? <span className="pp-mobile-school-link-description">{link.description}</span> : null}
                  </span>
                  <ChevronIcon className="pp-mobile-more-row-chevron" aria-hidden="true" />
                </a>
              )) : (
                <p className="pp-mobile-more-footer">{t("school.links.empty", { defaultValue: "No school-wide links have been added yet." })}</p>
              )}
            </div>
          </>
        ) : (
          <>
          <h2 className="pp-mobile-more-title">{t("parentPortal.mobile_nav.more", { defaultValue: "More" })}</h2>

          {menuSections
          .filter((section) => !section.hidden)
          .map((section, sectionIndex) => (
            <section className="pp-mobile-more-section" key={section.title || `section-${sectionIndex}`}>
              {section.title && <p className="pp-mobile-more-section-title">{section.title}</p>}
              <div className="pp-mobile-more-list">
                {section.items.map(({ key, label, Icon, badge, danger, onClick }) => (
                  <React.Fragment key={key}>
                    <button
                      type="button"
                      className={`pp-mobile-more-row ${danger ? "is-danger" : ""}`}
                      onClick={onClick}
                    >
                      <Icon className="pp-mobile-more-row-icon" aria-hidden="true" />
                      <span className="pp-mobile-more-row-label">{label}</span>
                      {badge && <span className="pp-mobile-more-row-badge">{badge}</span>}
                      <ChevronIcon className="pp-mobile-more-row-chevron" aria-hidden="true" />
                    </button>
                    {key === "actions" && activeTab === "actions" && showSchoolTasks && (
                      <div className="mb-12 ml-12 mt-1">
                        <ParentActionsSubnav
                          activeView={actionsView}
                          onChange={(view) => {
                            setActionsView(view);
                            setMoreOpen(false);
                          }}
                          openActionsCount={openActionsCount}
                          schoolTasksCount={schoolTasksCount}
                          showSchoolTasks={showSchoolTasks}
                          openActionsLabel={openActionsLabel}
                          schoolTasksLabel={schoolTasksLabel}
                          className="w-[min(15rem,calc(100vw-6rem))]"
                        />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </section>
          ))}

          <p className="pp-mobile-more-footer">
          {t("parentPortal.mobile_nav.product_name", { defaultValue: "Lumen Parent Portal" })}
          {appVersion ? <><br />{t("parentPortal.mobile_nav.version", { defaultValue: "Version {{version}}", version: appVersion })}</> : null}
          </p>
          </>
        )}
      </div>

      <nav
        className={`pp-mobile-nav ${moreOpen ? "is-hidden" : ""}`}
        aria-label={t("parentPortal.mobile_nav.navigation", { defaultValue: "Parent portal navigation" })}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {activeTab === "directory" && directorySearchOpen && directorySearch ? (
          <form
            className="pp-mobile-nav-search"
            onSubmit={(event) => {
              event.preventDefault();
              directorySearch.onSubmit();
            }}
          >
            <FiSearch aria-hidden="true" />
            <input
              ref={directoryInputRef}
              type="search"
              value={directorySearch.value}
              onChange={(event) => directorySearch.onChange(event.target.value)}
              placeholder={directorySearch.placeholder || t("parentPortal.directory.search_placeholder", { defaultValue: "Search parents or children..." })}
              disabled={directorySearch.loading}
              aria-label={t("parentPortal.directory.search", { defaultValue: "Search directory" })}
            />
            <button
              type="button"
              className="pp-mobile-nav-search-close"
              onClick={() => setDirectorySearchOpen(false)}
              aria-label={t("common.close", { defaultValue: "Close" })}
            >
              <FiX aria-hidden="true" />
            </button>
          </form>
        ) : (
        <div className="pp-mobile-nav-inner">
          {primaryTabs.map((tab) => {
            const Icon = ICONS[tab.key] || MoreIcon;
            const isActive = activeTab === tab.key && !moreOpen;

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  setMoreOpen(false);
                  if (tab.key === "directory") setDirectorySearchOpen(true);
                  setActiveTab(tab.key);
                }}
                className={`pp-mobile-nav-item ${isActive ? "is-active" : ""}`}
                aria-current={isActive ? "page" : undefined}
              >
                <Icon className="pp-mobile-nav-icon" aria-hidden="true" />
                <span className="pp-mobile-nav-label">
                  {tab.label}
                </span>
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => {
              setMoreView("main");
              setMoreOpen((open) => !open);
            }}
            className={`pp-mobile-nav-item ${moreOpen ? "is-active" : ""}`}
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
          >
            <MoreIcon className="pp-mobile-nav-icon" aria-hidden="true" />
            <span className="pp-mobile-nav-label">{t("parentPortal.mobile_nav.more", { defaultValue: "More" })}</span>
          </button>
        </div>
        )}
      </nav>
    </>
  );
}
